import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import {
  AssetView,
  AssetCondition,
  AssetCategoryView,
  CreateAssetInput,
  UpdateAssetInput,
} from "../../../../api/General/assets";

interface AssetFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (input: CreateAssetInput | UpdateAssetInput) => Promise<void>;
  asset?: AssetView | null;
  categories: AssetCategoryView[];
  saving: boolean;
}

const AssetFormModal: React.FC<AssetFormModalProps> = ({
  open,
  onClose,
  onSave,
  asset,
  categories,
  saving,
}) => {
  const { t } = useTranslation();
  const isEditing = !!asset;

  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState<number>(0);
  const [serialNumber, setSerialNumber] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [warrantyExpiry, setWarrantyExpiry] = useState("");
  const [condition, setCondition] = useState<AssetCondition>(AssetCondition.Good);
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (asset) {
      setName(asset.name);
      setCategoryId(asset.categoryId);
      setSerialNumber(asset.serialNumber ?? "");
      setPurchasePrice(asset.purchasePrice != null ? String(asset.purchasePrice) : "");
      setPurchaseDate(asset.purchaseDate ? asset.purchaseDate.split("T")[0] : "");
      setWarrantyExpiry(asset.warrantyExpiry ? asset.warrantyExpiry.split("T")[0] : "");
      setCondition(asset.condition);
      setLocation(asset.location ?? "");
      setNotes(asset.notes ?? "");
    } else {
      setName("");
      setCategoryId(categories.length > 0 ? categories[0].id : 0);
      setSerialNumber("");
      setPurchasePrice("");
      setPurchaseDate("");
      setWarrantyExpiry("");
      setCondition(AssetCondition.Good);
      setLocation("");
      setNotes("");
    }
    setErrors({});
  }, [asset, open, categories]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = t("assets.nameRequired");
    if (!categoryId) errs.categoryId = t("assets.categoryRequired");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const input: CreateAssetInput | UpdateAssetInput = {
      ...(isEditing ? { id: asset!.id } : {}),
      name: name.trim(),
      categoryId,
      serialNumber: serialNumber.trim() || undefined,
      purchasePrice: purchasePrice ? Number(purchasePrice) : undefined,
      purchaseDate: purchaseDate || undefined,
      warrantyExpiry: warrantyExpiry || undefined,
      condition,
      location: location.trim() || undefined,
      notes: notes.trim() || undefined,
    } as CreateAssetInput | UpdateAssetInput;

    await onSave(input);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="default">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t("assets.editAsset") : t("assets.addAsset")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("assets.name")} *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${errors.name ? "border-destructive" : "border-border"}`}
                data-testid="asset-name-input"
              />
              {errors.name && <p className="text-destructive text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("assets.category")} *</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(Number(e.target.value))}
                className={`w-full px-3 py-2 border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${errors.categoryId ? "border-destructive" : "border-border"}`}
                data-testid="asset-category-select"
              >
                <option value={0} disabled>{t("assets.selectCategory")}</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              {errors.categoryId && <p className="text-destructive text-xs mt-1">{errors.categoryId}</p>}
            </div>

            {/* Serial Number */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("assets.serialNumber")}</label>
              <input
                type="text"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="asset-serial-input"
              />
            </div>

            {/* Purchase Price & Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("assets.purchasePrice")}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="asset-price-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("assets.purchaseDate")}</label>
                <input
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="asset-purchase-date-input"
                />
              </div>
            </div>

            {/* Warranty Expiry */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("assets.warrantyExpiry")}</label>
              <input
                type="date"
                value={warrantyExpiry}
                onChange={(e) => setWarrantyExpiry(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="asset-warranty-input"
              />
            </div>

            {/* Condition */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("assets.condition")}</label>
              <select
                value={condition}
                onChange={(e) => setCondition(e.target.value as AssetCondition)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="asset-condition-select"
              >
                <option value={AssetCondition.Excellent}>{t("assets.conditionExcellent")}</option>
                <option value={AssetCondition.Good}>{t("assets.conditionGood")}</option>
                <option value={AssetCondition.Fair}>{t("assets.conditionFair")}</option>
                <option value={AssetCondition.Poor}>{t("assets.conditionPoor")}</option>
              </select>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("assets.location")}</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="asset-location-input"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("assets.notes")}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
                data-testid="asset-notes-input"
              />
            </div>
          </DialogBody>

          {/* Actions */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="asset-form-cancel"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              loading={saving}
              data-testid="asset-form-save"
            >
              {saving ? t("common.saving") : isEditing ? t("common.save") : t("assets.addAsset")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AssetFormModal;
